import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { NewOrderButton } from "@/components/NewOrderButton";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { useCurrentUser, userHasGroup } from "@/lib/auth";
import { useRealtime } from "@/lib/useRealtime";
import { ApiError } from "@/lib/apiClient";
import {
  listMaterialAnforderungen,
  type MaListItem,
  type MaStatus,
} from "@/lib/material";
import { MaDialog } from "@/pages/material/MaDialog";
import { MaListSection } from "@/pages/material/MaListSection";
import "@/pages/material/materialMobile.css";

const STATUS_FILTER_OPTIONS: { value: MaStatus | ""; label: string }[] = [
  { value: "", label: "Alle" },
  { value: "offen", label: "Offen" },
  { value: "in_bearbeitung", label: "In Bearbeitung" },
  { value: "erledigt", label: "Erledigt" },
  { value: "abgelehnt", label: "Storniert" },
];

export function MaterialIndexPage() {
  const { user } = useCurrentUser();
  const isBueroOrAdmin = useMemo(() => {
    if (!user) return false;
    return (
      userHasGroup(user, "App-Admins") ||
      userHasGroup(user, "Büro") ||
      userHasGroup(user, "App-Tester")
    );
  }, [user]);

  const [items, setItems] = useState<MaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<MaStatus | "">("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [claimedOnly, setClaimedOnly] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeMaId, setActiveMaId] = useState<number | null>(null);
  const [presetAuftragId, setPresetAuftragId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  // Deep-Link: ?id=123 öffnet eine bestehende MA,
  // ?create_auftrag_id=456 (und ältere ?auftrag_id=) eine neue.
  useEffect(() => {
    const idParam = searchParams.get("id");
    const auftragParam =
      searchParams.get("create_auftrag_id") || searchParams.get("auftrag_id");
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (idParam) {
      const parsed = Number(idParam);
      if (Number.isFinite(parsed) && parsed > 0) {
        setActiveMaId(parsed);
        setPresetAuftragId(null);
        setDialogOpen(true);
      }
      next.delete("id");
      changed = true;
    } else if (auftragParam) {
      const parsed = Number(auftragParam);
      if (Number.isFinite(parsed) && parsed > 0) {
        setActiveMaId(null);
        setPresetAuftragId(parsed);
        setDialogOpen(true);
      }
      next.delete("create_auftrag_id");
      next.delete("auftrag_id");
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // Nur einmal beim Mount ausführen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const list = await listMaterialAnforderungen({
        status: statusFilter || undefined,
        q: debouncedSearch || undefined,
        claimedOnly: isBueroOrAdmin && claimedOnly ? true : false,
      });
      setItems(list || []);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Liste konnte nicht geladen werden.";
      setError(message);
      if (!options?.silent) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, isBueroOrAdmin, claimedOnly]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime-Änderungen still einpflegen – die Liste bleibt dabei stehen.
  useRealtime({
    scopes: ["material-anforderungen:list"],
    domains: ["material_anforderungen", "material-anforderungen", "anfragen", "orders"],
    events: [
      "material_anforderungen_changed",
      "material-anforderungen_changed",
      "material_anforderung_changed",
      "anfragen_changed",
      "orders_changed",
    ],
    onChange: () => void reload({ silent: true }),
    fallbackIntervalMs: 180_000,
  });

  function openNew() {
    setActiveMaId(null);
    setPresetAuftragId(null);
    setDialogOpen(true);
  }
  function openExisting(id: number) {
    setActiveMaId(id);
    setPresetAuftragId(null);
    setDialogOpen(true);
  }
  function closeDialog() {
    setDialogOpen(false);
    setActiveMaId(null);
    setPresetAuftragId(null);
  }

  return (
    <AppShell
      title="Materialanforderungen"
      className="material-mobile-shell material-mobile-shell--with-fab"
      subtitle={
        isBueroOrAdmin
          ? "Übersicht aller Anforderungen – konvertierbar in TA oder TK."
          : "Deine Materialanforderungen. Foto, Skizze oder Liste – das Büro kümmert sich."
      }
      actions={
        <div className="hidden flex-wrap gap-2 sm:flex">
          <NewOrderButton variant="secondary" />
          <Button data-tour="material-neu" onClick={openNew}>+ Neue Anforderung</Button>
        </div>
      }
    >
      <section className="ma-filters rounded-ios border border-separator bg-card p-4 shadow-ios max-sm:border-none max-sm:bg-transparent max-sm:p-0 max-sm:shadow-none">
        <div
          className="inline-flex flex-wrap gap-1 rounded-full bg-fill-tertiary p-1 ring-1 ring-separator/35"
          role="group"
          aria-label="Status filtern"
        >
          {STATUS_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value || "all"}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              className={clsx(
                "rounded-full px-3 py-1.5 text-sm font-semibold transition",
                statusFilter === option.value
                  ? "bg-card text-label shadow-sm ring-1 ring-separator/40"
                  : "text-label-secondary hover:text-label",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 max-sm:flex-col max-sm:items-stretch">
          <input
            type="search"
            placeholder="Suchen (Nummer, Text, Notiz …)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="eh-input flex-1 min-w-[14rem]"
          />
          {isBueroOrAdmin && (
            <label className="inline-flex items-center gap-2 text-sm text-label max-sm:ml-0">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                checked={claimedOnly}
                onChange={(e) => setClaimedOnly(e.target.checked)}
              />
              <span>nur übernommene</span>
            </label>
          )}
        </div>
      </section>

      {error && (
        <Alert variant="error" title="Liste konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section>
        {items.length === 0 && !loading ? (
          <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center text-sm text-label-secondary shadow-ios">
            Keine Materialanforderungen.
            <div className="mt-3">
              <Button data-tour="material-neu" onClick={openNew}>+ Neue Anforderung</Button>
            </div>
          </div>
        ) : (
          <MaListSection items={items} loading={loading} onOpen={openExisting} />
        )}
      </section>

      <MaDialog
        open={dialogOpen}
        onClose={closeDialog}
        onChanged={() => void reload()}
        maId={activeMaId}
        initialAuftragId={presetAuftragId}
        canProcess={isBueroOrAdmin}
      />

      <button
        type="button"
        className="material-mobile-fab sm:hidden"
        data-tour="material-neu"
        onClick={openNew}
        aria-label="Neue Materialanforderung"
      >
        <span aria-hidden>+</span>
        <span>Neue Anforderung</span>
      </button>
    </AppShell>
  );
}
